import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { LegacyChatMessage } from "../core/types/tsx/state/ChatContext.types";

import chatMockRaw from "../mocks/chat-expanded.sample.json";
import {
  ChatContext,
  type ChatMessage,
  type ChatSender,
  type ChatMockData,
  type ChatState,
  type Conversation,
} from "./chat-store";


function normalizeMockMessages(
  raw: Record<string, Array<ChatMessage | LegacyChatMessage>>,
): Record<string, ChatMessage[]> {
  const out: Record<string, ChatMessage[]> = {};
  for (const [conversationId, list] of Object.entries(raw ?? {})) {
    out[conversationId] = (list ?? []).map((m) => {
      if ("sender" in m) return m as ChatMessage;
      const legacy = m as LegacyChatMessage;
      const sender: ChatSender = legacy.from === "me" ? "client" : "provider";
      if (legacy.kind === "quote") {
        return {
          id: legacy.id,
          sender,
          kind: "quote",
          quoteCode: legacy.quoteCode ?? "COT-000",
          subtitle: legacy.subtitle ?? "",
          items: (legacy.items ?? []).map((it) => ({ label: it.label, amount: it.amount })),
        };
      }
      return {
        id: legacy.id,
        sender,
        kind: "text",
        text: legacy.text ?? "",
        time: legacy.time ?? "",
      };
    });
  }
  return out;
}

function nowTimeLabel() {
  return new Date().toLocaleTimeString("es-VE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const chatMock = chatMockRaw as unknown as ChatMockData;

  const [conversations, setConversations] = useState<Conversation[]>(
    chatMock.conversations ?? [],
  );

  const [activeConversationId, setActiveConversationId] = useState<string>(
    chatMock.conversations?.[0]?.id ?? conversations[0]?.id ?? "cleaning",
  );

  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, ChatMessage[]>>(() =>
    normalizeMockMessages(chatMock.messagesByConversation ?? {}),
  );

  const markConversationRead = useCallback((id: string) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  }, []);

  const sendTextMessage = useCallback((conversationId: string, text: string, sender: ChatSender) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const next: ChatMessage = {
      id: String(Date.now()),
      sender,
      kind: "text",
      text: trimmed,
      time: nowTimeLabel(),
    };

    setMessagesByConversation((prev) => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] ?? []), next],
    }));

    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, preview: trimmed, timeLabel: next.time } : c)),
    );
  }, []);

  const value = useMemo<ChatState>(() => {
    return {
      conversations,
      activeConversationId,
      setActiveConversationId,
      markConversationRead,
      messagesByConversation,
      sendTextMessage,
    };
  }, [conversations, activeConversationId, markConversationRead, messagesByConversation, sendTextMessage]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
